import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from "react";
import type { Plan, Program } from "@heraion/shared";

import { useLocale } from "../primitives/useLocale";
import { planKey, programCopyKeys } from "./copyKeys";
import { createPendingCopiesStore } from "./pendingCopiesStore";
import { useProgramsInvalidator } from "./programsInvalidator";

export { createPendingCopiesStore, type PendingCopiesStore } from "./pendingCopiesStore";
export { modelKey, nameKey, officialCopyKeys, planKey, programCopyKeys, queuedCopyKeys } from "./copyKeys";

// #475: copies a QUEUED write promised that the network list does not show
// yet. Two ways in: the `queued` answer (hold), and the host's queue read
// at mount (seams.ts PendingCreatesSource, mobile only), so a restart with
// the queue still full keeps them.
// Reconcile: an entry (the keys one copy answers to: name, origin model) is
// dropped as soon as the network list holds a program with any of them.
// A plan answers to `plan:<name>`, reconciled by the plans list: the two
// lists share the store and never drop each other's entries.

type Entry = readonly string[];

/** `listedKeys`: the keys of the network list this instance reads (null: not
 *  read yet, so nothing is reconciled). */
function usePendingCopies(listedKeys: ReadonlySet<string> | null) {
  const inv = useProgramsInvalidator();
  const [own] = useState(createPendingCopiesStore);
  const store = inv ? inv.copies : own;
  const pendingKeys = useSyncExternalStore(store.subscribe, store.snapshot, store.snapshot);

  useEffect(() => {
    if (listedKeys) store.reconcile(listedKeys);
  }, [listedKeys, store]);

  const holdCopy = useCallback((keys: Entry) => store.hold(keys), [store]);
  return { pendingKeys, holdCopy };
}

/** `listed`: the programs list this instance reads (null: not read yet).
 *  Returns the keys still pending and `hold`, to be called when a copy's
 *  write answers `queued`. */
export function usePendingProgramCopies(listed: ReadonlyArray<Program> | null) {
  const lang = useLocale();
  const listedKeys = useMemo(
    () => (listed ? new Set(listed.flatMap((p) => programCopyKeys(p, lang))) : null),
    [listed, lang],
  );
  return usePendingCopies(listedKeys);
}

/** Same, over the plans list: a plan copy answers to its name. */
export function usePendingPlanCopies(listed: ReadonlyArray<Plan> | null) {
  const listedKeys = useMemo(
    () => (listed ? new Set(listed.map((p) => planKey(p.name))) : null),
    [listed],
  );
  return usePendingCopies(listedKeys);
}
